import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '@redux/store'

import {
  GETVariableDTO,
  ObjectDataRecord,
  TimelineCellItemDragState,
  TimeLineGridDataRecord,
} from '@types'

type InitialState = {
  totalsRows: ({ isLoading?: boolean } & TimeLineGridDataRecord)[]
  variables: Record<string, GETVariableDTO>
  loadingItemIds: string[]
  cellItemDragState: TimelineCellItemDragState | null
  selectedCellsCount: number
  selectedEventsCount: number
}

const initialState: InitialState = {
  totalsRows: [],
  variables: {},
  loadingItemIds: [],
  cellItemDragState: null,
  selectedCellsCount: 0,
  selectedEventsCount: 0,
}

const timeLineSlice = createSlice({
  name: 'timeLineGrid',
  initialState,
  reducers: {
    initializeDynamicColumnsTotals: (
      state: ObjectDataRecord,
      action: PayloadAction<TimeLineGridDataRecord[]>
    ) => {
      state.totalsRows = action.payload
    },
    setDynamicColumnsIsLoading: (
      state: InitialState,
      action: PayloadAction<{
        rowIndex: number
        key: string
        isLoading: boolean
      }>
    ) => {
      const { rowIndex, key } = action.payload

      state.totalsRows[rowIndex] = {
        ...state.totalsRows[rowIndex],
        [key]: [{ isLoading: true }],
      }
    },
    setDynamicColumnsTotalData: (
      state: InitialState,
      action: PayloadAction<{
        rowIndex: number
        key: string
        value: ObjectDataRecord
      }>
    ) => {
      const { rowIndex, key, value } = action.payload

      state.totalsRows[rowIndex] = {
        ...state.totalsRows[rowIndex],
        [key]: [{ isLoading: false, value }],
      }
    },
    setTimelineVariable: (
      state: InitialState,
      action: PayloadAction<{
        variable: GETVariableDTO
      }>
    ) => {
      state.variables[action.payload.variable.internalId] = action.payload.variable
    },
    setTimelineVariables: (
      state: InitialState,
      action: PayloadAction<{
        variables: Record<string, GETVariableDTO>
      }>
    ) => {
      state.variables = {
        ...state.variables,
        ...action.payload.variables,
      }
    },
    addLoadingItemId: (state: InitialState, action: PayloadAction<string>) => {
      if (!state.loadingItemIds.includes(action.payload)) {
        state.loadingItemIds.push(action.payload)
      }
    },
    removeLoadingItemId: (state: InitialState, action: PayloadAction<string>) => {
      state.loadingItemIds = state.loadingItemIds.filter(id => id !== action.payload)
    },
    setCellItemDragState: (
      state: InitialState,
      action: PayloadAction<TimelineCellItemDragState>
    ) => {
      state.cellItemDragState = action.payload
    },
    clearCellItemDragState: (state: InitialState) => {
      state.cellItemDragState = null
    },
    setSelectionCounts: (
      state: InitialState,
      action: PayloadAction<{ selectedCellsCount: number; selectedEventsCount: number }>
    ) => {
      state.selectedCellsCount = action.payload.selectedCellsCount
      state.selectedEventsCount = action.payload.selectedEventsCount
    },
  },
})

export default timeLineSlice.reducer
export const {
  setDynamicColumnsIsLoading,
  setDynamicColumnsTotalData,
  initializeDynamicColumnsTotals,
  setTimelineVariables,
  addLoadingItemId,
  removeLoadingItemId,
  setCellItemDragState,
  clearCellItemDragState,
  setSelectionCounts,
} = timeLineSlice.actions

export const selectTimelineSelectionCounts = (state: RootState) => ({
  selectedCellsCount: state.timeLineGrid.selectedCellsCount,
  selectedEventsCount: state.timeLineGrid.selectedEventsCount,
})
